import { ChangeEvent } from 'react';
import './FormColorInput.scss';

import { Stack, useTheme } from '@mui/material';

import { componentClassName } from '@dw/utils';

import { HStack } from '../HStack';
import { FormTextInput } from './FormTextInput';
import { useFormContext } from './Form.Context';

/**
 * What counts as a colour here, as regex SOURCE strings so they can be handed to the input's
 * `pattern` attribute as well as tested in JS.
 *
 * That attribute is the point: it is what puts an unparseable value into the browser's `:invalid`
 * set, which is what `Form` scans before it submits. Without it the regex below only chose what the
 * swatch *displayed*, and a typed `#12345` sailed past the dialog and 400'd at the API.
 *
 * **Two patterns, because the APIs behind this control disagree** — and the client must be a subset
 * of whichever one it is talking to, never a superset. The default accepts the three-digit shorthand
 * because invoice branding's API does (and schools have `#0ab` values stored); `sixDigitOnly` is for
 * the stricter APIs. Defaulting to the strict form would make the branding page unsavable for any
 * school holding a shorthand colour — the whole section, not just the field.
 */
const ANY_HEX_SOURCE = '#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})';
const SIX_DIGIT_HEX_SOURCE = '#[0-9a-fA-F]{6}';

/** Only decides what the native swatch shows, so it is deliberately the permissive one. */
const HEX_PATTERN = new RegExp(`^${ANY_HEX_SOURCE}$`);

interface IFormColorInputProps<T> {
    name: keyof T;
    label?: string;
    helperText?: string;
    disabled?: boolean;
    /**
     * Whether a colour must be chosen. NOT the default: `pattern` alone does not reject an EMPTY
     * field (the browser only applies it to a non-empty value), so a control whose API rejects a
     * blank has to say so here. Left off, a blank is allowed through — which is right for the
     * optional branding colours and wrong for a notice theme.
     */
    required?: boolean;
    /**
     * Refuse the `#abc` shorthand. Set it when the API behind this field only accepts `#RRGGBB`, so
     * the client cannot accept a value the save will reject.
     */
    sixDigitOnly?: boolean;
}

/**
 * Form-bound colour control: a native colour swatch paired with a hex text field, both editing the
 * same field. Drop it inside a `Form` like any other `Form*` input.
 */
export function FormColorInput<T>(props: IFormColorInputProps<T>) {
    const { name, label, helperText, disabled, required, sixDigitOnly } = props;

    const { formData, handleFormChange, disabled: formDisabled } = useFormContext<T>();
    const { palette } = useTheme();

    const cn = componentClassName('form-color-input');

    const rawValue = formData[name];
    const currentValue = typeof rawValue === 'string' ? rawValue : '';
    const swatchValue = HEX_PATTERN.test(currentValue) ? currentValue : palette.primary.main;
    const isDisabled = disabled || formDisabled;

    const handleSwatchChange = (e: ChangeEvent<HTMLInputElement>) => {
        handleFormChange(name, e.target.value.toUpperCase());
    };

    return (
        <HStack className={cn()} breakpoint={false} columnGap={2} alignItems='flex-start'>
            <input
                type='color'
                className={cn('swatch')}
                value={swatchValue}
                onChange={handleSwatchChange}
                disabled={isDisabled}
                aria-label={label ?? String(name)}
            />

            <Stack flex={1}>
                <FormTextInput<T>
                    name={name}
                    label={label}
                    margin='none'
                    fullWidth
                    upperCase
                    maxLength={7}
                    required={required}
                    pattern={sixDigitOnly ? SIX_DIGIT_HEX_SOURCE : ANY_HEX_SOURCE}
                    helperText={helperText}
                    disabled={isDisabled}
                />
            </Stack>
        </HStack>
    );
}
